import getClient from "./get-client";

/**
 * Dictado remoto: el computador pide, el celular dicta.
 *
 * El médico está en el computador y quiere hablarle al celular, que tiene mejor
 * micrófono y está más cerca de la boca. El computador deja un pedido abierto; el
 * celular, con la misma sesión ya iniciada, lo ve y ofrece tomarlo.
 *
 * ⚠️ El celular igual tiene que tocar un botón, y no hay forma de evitarlo:
 * ningún navegador enciende el micrófono sin un gesto del usuario EN ESE APARATO.
 * Lo que sí se elimina es el resto de la fricción: el celular ya sabe qué
 * paciente y qué campo, así que el médico no busca nada.
 *
 * Sondeo y no WebSocket: el backend es WSGI puro, no habla WebSocket. El turno ya
 * usa sondeo para traer lo que se dictó en otro aparato — mismo patrón, una pieza
 * menos que mantener.
 */

const BASE = "/api/medical_analysis/mic/request/";

/** El computador abre el pedido. Devuelve el pedido creado. */
export async function pedirDictadoRemoto({ tab, tabId, unidadId, row, column, paciente }) {
  const { data } = await getClient().post(BASE, {
    tab,
    tab_id: tabId ?? null,
    unidad_id: unidadId ?? null,
    row,
    column,
    paciente: paciente || "",
  });
  return data;
}

/** El celular pregunta si hay un pedido abierto. `null` si no hay. */
export async function mirarPedidoAbierto() {
  const { data } = await getClient().get(BASE);
  return data?.pedido || null;
}

/** El computador mira en qué quedó su pedido. */
export async function estadoDelPedido(id) {
  const { data } = await getClient().get(`${BASE}${id}/`);
  return data;
}

/**
 * El celular lo toma. Devuelve `{ tomado: true, pedido }`, o `tomado: false` si
 * otro aparato se adelantó o el computador ya lo canceló — que NO es un error del
 * celular, así que no se lanza excepción: se le devuelve el estado y decide.
 */
export async function tomarPedido(id) {
  try {
    const { data } = await getClient().post(`${BASE}${id}/claim/`, {});
    return { tomado: true, pedido: data };
  } catch (err) {
    if (err?.response?.status === 409) {
      return { tomado: false, pedido: err.response.data };
    }
    throw err;
  }
}

/** Cierra el pedido. `ok` = se dictó de verdad; si no, queda cancelado. */
export async function cerrarPedido(id, ok = true) {
  try {
    const { data } = await getClient().post(`${BASE}${id}/close/`, {
      resultado: ok ? "ok" : "cancel",
    });
    return data;
  } catch (_) {
    // Cerrar es higiene, no parte del flujo clínico: si falla, el pedido vence
    // solo a los 90 s. Nunca puede romperle nada al médico.
    return null;
  }
}

/**
 * El celular sube un TRAMO dictado. No escribe en la ficha: manda el texto y el
 * computador lo coloca donde tenga el cursor.
 *
 * Un tramo es lo que se dijo entre dos silencios. El corte lo pone la voz del
 * medico (`speech_final` de Deepgram), no un boton: asi puede dictar una frase,
 * mover el cursor a otra casilla, y seguir hablando sin tocar nada.
 */
export async function mandarTramo(id, texto) {
  const limpio = String(texto || "").trim();
  if (!limpio) return null;
  const { data } = await getClient().post(`${BASE}${id}/segment/`, { texto: limpio });
  return data;
}

/**
 * El computador se lleva los tramos nuevos.
 *
 * El backend YA NO marca entregado al leer. Antes lo hacia, y eso era entrega
 * "a lo mas una vez": si la pestana moria entre el GET y la insercion, o el
 * medico recargaba, ese tramo quedaba marcado como entregado y no volvia nunca —
 * texto dictado perdido, y en el celular tampoco queda copia.
 *
 * Ahora el que acusa es este aparato, con `acusarTramos`, recien cuando el texto
 * esta a salvo de su lado. `desde` sigue evitando reprocesar dentro de una misma
 * sesion; tras una recarga vuelve a 0 y lo no acusado se recupera.
 */
export async function traerTramos(id, desde = 0) {
  const { data } = await getClient().get(`${BASE}${id}/segments/`, { params: { desde } });
  return { segmentos: data?.segmentos || [], status: data?.status || null };
}

/**
 * Acusa recibo de los tramos que ya quedaron a salvo. Lo que no se acusa se
 * vuelve a ofrecer: mejor entregar dos veces que perder una frase clinica.
 *
 * No lanza: si el acuse no llega, el peor caso es que el tramo se vuelva a
 * ofrecer, y de eso ya se protege el llamador. Fallar el acuse NUNCA puede
 * interrumpir la insercion del texto.
 */
export async function acusarTramos(id, ids) {
  const limpios = (ids || []).map(Number).filter((n) => Number.isFinite(n));
  if (!id || !limpios.length) return 0;
  try {
    const { data } = await getClient().post(`${BASE}${id}/segments/ack/`, { ids: limpios });
    return data?.acusados ?? 0;
  } catch (_) {
    return 0;
  }
}
